import { Form, FormInstance } from "antd";
import { useEffect, useState } from "react";
import { SearchQuery } from "../code/search-query/search-query";

/**
 * Owns the boilerplate state + handlers shared by every searchable list-grid wrapper
 * (search form validity, the Data `context` reload signal, loading, clear). The grid
 * wrappers for Users/Managers/Employees/Consultants/Collaborators were
 * near-identical clones of this logic; they now compose it (directly, or via
 * <ManagedGridView>). Genuinely-bespoke grids (CompaniesView drill-down, CompanyTreeView)
 * opt out and keep their own state.
 *
 * IMPORTANT: search/trigger must publish a NEW `context` object reference so the virtual
 * antd Table in Data re-fires its `useEffect([context])` and drops its row cache. Do not
 * memoize `context` into a stable identity.
 */
export interface UseManagedGridOptions {
  readonly trigger?: string;
  readonly mapQuery: (values: any) => SearchQuery;
  readonly onSearch?: (query: SearchQuery) => void;
  /** Value `valuesValid` is reset to on Clear (default true; normalizes a prior divergence). */
  readonly clearResetsValid?: boolean;
  /** Pre-populate the search form on mount (e.g. a work-queue defaulting to a status filter). */
  readonly initialValues?: Record<string, any>;
  /** When true (with `initialValues`), seed the form + run an initial search on mount so the grid loads immediately. */
  readonly autoSearch?: boolean;
}

export interface ManagedGrid {
  readonly form: FormInstance<any>;
  readonly values: any;
  readonly loading: boolean;
  readonly valuesValid: boolean;
  readonly context: { readonly searchQuery: SearchQuery };
  readonly onLoading: (loading: boolean) => void;
  readonly onSearchData: () => void;
  readonly onClearData: () => void;
}

const emptyQuery = (): SearchQuery => ({ conditions: [] });

export function useManagedGrid(options: UseManagedGridOptions): ManagedGrid {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [valuesValid, setValuesValid] = useState(false);
  const [context, setContext] = useState<{ searchQuery: SearchQuery }>({
    searchQuery: emptyQuery(),
  });

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  // Re-publish a fresh context reference so Data reloads when the parent bumps `trigger`.
  useEffect(() => {
    setContext((current) => ({ ...current }));
  }, [options.trigger]);

  // Optional: seed the form and run an initial search once on mount (work-queue default filter).
  useEffect(() => {
    if (options.autoSearch && options.initialValues) {
      form.setFieldsValue(options.initialValues);
      const searchQuery = options.mapQuery(options.initialValues);
      setValuesValid(true);
      setContext({ searchQuery });
      options.onSearch?.(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSearchData(): void {
    const searchQuery = options.mapQuery(values);
    setContext({ searchQuery });
    options.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(options.clearResetsValid ?? true);
    form.resetFields();
    // Re-publish the cleared query so a parent that captured the last search (e.g. for Export)
    // matches the now-cleared grid instead of exporting the stale filter.
    const cleared = emptyQuery();
    setContext({ searchQuery: cleared });
    options.onSearch?.(cleared);
  }

  return {
    form,
    values,
    loading,
    valuesValid,
    context,
    onLoading: setLoading,
    onSearchData,
    onClearData,
  };
}
